import { File } from '@awesome-cordova-plugins/file/ngx';
import { Logger } from './app-error-logger';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Utils } from './utils';

const log = new Logger('FileHelper');

export class FileHelper {
    static file: File;

    static getFilename(path: string) {
        if (path && typeof (path) === 'string') {
            // Extract just the filename. Result example: cdv_photo_003.jpg
            // Eventuelles ? und alles dahinter entfernen
            // Beispiel: file:///storage/emulated/0/Android/data/de.recomobil.app/cache/1000006939.jpg?1700814405156

            let idx = path.indexOf('?');

            if (idx > 0) {
                path = path.substring(0, idx);
            }

            return path.substr(path.lastIndexOf('/') + 1);
        }

        return null;
    }

    static getDirectory(path: string) {
        if (path && path.startsWith('file:')) {
            return path.substr(0, path.lastIndexOf('/') + 1);
        }

        return null;
    }

    static async deleteFile(fileUri: string) {
        log?.debug('deleteFile: ' + fileUri);

        if (fileUri && fileUri.startsWith('file:')) {
            const filename = FileHelper.getFilename(fileUri);
            const directory = FileHelper.getDirectory(fileUri);

            try {
                await FileHelper.file.removeFile(directory, filename);
            } catch (err) {
                const message = err?.message || '';

                if (message && message.includes('NOT_FOUND_ERR')) {
                    // Datei nicht gefunden, ist ok.
                    log?.debug('Datei nicht vorhanden. Wird nicht gelöscht: ' + fileUri);
                    return;
                }

                log.warn('error removing file ' + fileUri + ': ' + Utils.getErrorMessage(err, true), err);
            }
        }
    }

    static removeIllegalFilenameChars(filename: string): string {
        return filename.replace(/[/\\?%*:|"<>]/g, '-');
    }

    static getFileIcon(mimeType: string) {
        if (mimeType) {
            if (mimeType.startsWith('image/jp')) {
                return 'jpg';
            } else if (mimeType.startsWith('image/png')) {
                return 'png';
            } else if (mimeType === 'application/pdf') {
                return 'pdf';
            }
        }

        return 'document';
    }

    // static async checkFile(filePath: string): Promise<boolean> {
    //     log?.debug('checkFile: ' + filePath);

    //     if (!filePath) {
    //         return false;
    //     }

    //     const directory = FileHelper.getDirectory(filePath);
    //     const filename = FileHelper.getFilename(filePath);

    //     let fileExists = false;

    //     try {
    //         fileExists = await FileHelper.file.checkFile(directory, filename);
    //     } catch (err) {
    //         log.warn('checkFile error ' + filePath + ': ' + err?.message, err);
    //     }

    //     return fileExists;
    // }

    static async checkFileExistsByUri(fileUri: string): Promise<boolean> {
        log?.debug('checkFileUriExtis: ' + fileUri);

        if (!fileUri) {
            return false;
        }

        const directoryAndPath = this.getDirectoryAndPath(fileUri);

        return this.checkFileExists(directoryAndPath.directory, directoryAndPath.path);
    }

    static async checkFileExists(directory: Directory, filePath: string): Promise<boolean> {
        log?.debug('checkFileExists: ' + filePath);

        if (!filePath) {
            return false;
        }

        try {
            await Filesystem.stat({
                directory: directory,
                path: filePath
            });

            return true;
        } catch (err) {
            const code = err?.code || '';

            // siehe: https://capacitorjs.com/docs/apis/filesystem#errors
            if (code === 'OS-PLUG-FILE-0008') {
                // Operation failed because file does not exist.
                return false;
            }

            const message = Utils.getErrorMessage(err);

            if (message && message.includes('does not exist')) {
                return false;
            }

            log.warn('checkFileExists error ' + filePath + ': ' + Utils.getErrorMessage(err), err);

            return false;
        }
    }

    /**
     * Wandelt eine URI in das Directory und den Pfad um, die von Capacitor Filesystem verwendet werden können.
     */
    static getDirectoryAndPath(uri: string) {
        const map = [
            { dir: Directory.Cache, key: '/cache/' },
            { dir: Directory.Data, key: '/files/' },
            { dir: Directory.Documents, key: '/documents/' },
            { dir: Directory.External, key: '/Android/data/' },
            { dir: Directory.ExternalStorage, key: '/storage/emulated/0/' },
        ];

        for (const entry of map) {
            const idx = uri.indexOf(entry.key);

            if (idx !== -1) {
                return {
                    directory: entry.dir,
                    path: uri.substring(idx + entry.key.length)
                };
            }
        }

        throw new Error('Unbekannter Speicherort: ' + uri);
    }

    static async rmdir(directory: Directory, path: string) {
        log?.debug('rmdir: ' + path);

        try {
            await Filesystem.rmdir({
                path: path,
                directory: directory,
                recursive: true
            });
        } catch (err) {
            const code = err?.code || '';

            // siehe: https://capacitorjs.com/docs/apis/filesystem#errors
            if (code === 'OS-PLUG-FILE-0008') {
                // Operation failed because file does not exist.
                return;
            }

            log?.debug('rmdir err: ' + Utils.getErrorMessage(err, true), err);

            const msg = Utils.getErrorMessage(err);

            if (msg.includes('already exists')) {
                // passt. Verzeichnis existiert bereits
                return;
            }

            log.warn('rmdir: ' + msg);
        }
    }

    static async mkdir(directory: Directory, path: string) {
        log?.debug('mkdir: ' + path);

        try {
            if (!path) {
                return;
            }

            await Filesystem.mkdir({
                path: path,
                directory: directory,
                recursive: true
            });
        } catch (err) {
            const msg = Utils.getErrorMessage(err, true);

            if (msg.includes('already exists')) {
                // passt. Verzeichnis existiert bereits
                return;
            }

            log.warn('mkdir: ' + msg);
        }
    }


    // static async mkdir(directory: Directory, path: string): Promise<void> {
    //     try {
    //         log?.debug(`mkdir: ${directory} | ${path}`);

    //         if (path) {
    //             try {
    //                 await Filesystem.mkdir({
    //                     directory: directory,
    //                     path: path,
    //                     recursive: true
    //                 });
    //             } catch (err) {
    //                 const msg = Utils.getErrorMessage(err);

    //                 if (msg && msg.indexOf('Directory exists') >= 0) {
    //                     // ignore. Alles gut.
    //                 } else {
    //                     throw err;
    //                 }
    //             }
    //         }
    //     } catch (err) {
    //         // ignore. Verzeichnis schon vorhanden
    //         log?.debug('mkdir: ' + Utils.getErrorMessage(err));
    //     }
    // }

    static async deleteFileEx(directory: Directory, path: string) {
        log?.debug('deleteFileEx: ' + path);

        try {
            await Filesystem.deleteFile({
                path: path,
                directory: directory
            });
        } catch (err) {
            log?.debug('deleteFile err: ' + Utils.getErrorMessage(err, true), err);
        }
    }
}
